import { Component, ComponentFactoryResolver, ViewEncapsulation, ElementRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ModalService, ModalSize, SelectListCellRenderer, Secure } from '@cv-invision/core';
import { ICellRendererParams } from 'ag-grid-community';
import { URLS, LOGS, ORIGIN, URLS_FIELDS } from '../../model/nexsys-monitor.constant';
import { OrdersLogComponent } from '../orders-log/orders-log.component';
import { DROPDOWN_BUTTON_ITEM_TYPE, DropdownButtonItem } from '@cv-invision/cv.component-library.ux';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PERMISSION } from '../../../model/global-configuration.constant';

@Component({
	selector: 'cv-custom-orders-action-grid-cell',
	templateUrl: './custom-action-grid-cell.component.html',
	encapsulation: ViewEncapsulation.None,
	styleUrls: ['./custom-action-grid-cell.component.scss']
})
export class CustomActionNexsysOrdersCellComponent extends SelectListCellRenderer implements OnDestroy {
	private atComponentDestroy: Subject<any> = new Subject<any>();
	public params: ICellRendererParams;
	public cellValue: any;
	isValuTrust: boolean;
	isAssociated: boolean;
	isAssociatedAddendum: boolean;
	logDropdownItems: Array<DropdownButtonItem>;

	constructor(
		private router: Router,
		private componentFactoryResolver: ComponentFactoryResolver,
		private modalService: ModalService,
		public elementRef: ElementRef
	) {
		super(elementRef);
	}

	/**
	 * When the component renders, it got the possible value
	 * @param params
	 */
	agInit(params: ICellRendererParams): void {
		this.params = params;
		this.cellValue = params.data;
		this.isValuTrust = this.cellValue.provider === 'valutrust';
		this.isAssociated = !!this.cellValue.associatedProviderId;
		this.isAssociatedAddendum = !!this.cellValue.associatedAddendumProviderId;

		this.buildLogDropDownOptions();
	}

	/**
	 * Build Drop Down Options
	 */
	buildLogDropDownOptions(): void {
		this.logDropdownItems = [
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.ORDER,
				icon: 'fa-file-text-o'
			}
		];

		if (this.cellValue.provider === 'nexsys') {
			this.addNexsysItem();
		}

		if (this.cellValue.provider === 'valutrust') {
			this.addValutrustItem();
		}

		if (this.cellValue.provider === 'xpanse') {
			this.addXpanseItem();
		}

		if (this.cellValue.provider === 'fnc') {
			this.addFNCItem();
		}

		if (this.cellValue.provider === 'evo') {
			this.addEVOItem();
		}

		if (this.cellValue.origin === 'ClassOrderEntry') {
			this.addOrderExternalItem();
		}

		if (this.cellValue.provider === 'valuelink') {
			this.addValuelinkItem();
		}

		if (this.cellValue.provider === 'valuelinklender') {
			this.addValuelinkLenderItem();
		}

		if (this.cellValue.provider === 'connexions') {
			this.addConnexionsItem();
		}

		if (this.cellValue.provider === 'lendingqb') {
			this.addLendingQBItem();
		}
		if (this.cellValue.provider === 'landgorilla') {
			this.addLandGorillaItem();
		}
		if (this.cellValue.provider === 'fics') {
			this.addFicsItem();
		}
		if (this.cellValue.provider === 'lendingpad') {
			this.addLendingpadItem();
		}

		if (this.cellValue.addendumInfo) {
			this.logDropdownItems.push(
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
				},
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
					label: LOGS.ADDENDUM,
					icon: 'fa-file-text-o'
				}
			);
		}

		if (this.cellValue.associatedProviderId) {
			this.logDropdownItems.push(
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
				},
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
					label: LOGS.ASSOCIATED_LOGS,
					icon: 'fa-file-text-o'
				}
			);
		}

		if (this.cellValue.associatedAddendumProviderId) {
			this.logDropdownItems.push(
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
				},
				{
					type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
					label: LOGS.ASSOCIATED_ADDENDUM_LOGS,
					icon: 'fa-file-text-o'
				}
			);
		}
	}

	@Secure(PERMISSION.NEXSYS)
	private addNexsysItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.NEXSYS,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.VALUTRUST)
	private addValutrustItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.VALUTRUST,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.XPANSE)
	private addXpanseItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.XPANSE,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.FNC)
	private addFNCItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.FNC,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.EVO)
	private addEVOItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.EVO,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.ORDERS_EXTERNAL)
	private addOrderExternalItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.ORDERSEXTERNAL,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.ORDERS_EXTERNAL)
	private addValuelinkItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.VALUELINK,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.ORDERS_EXTERNAL)
	private addValuelinkLenderItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.VALUELINK_LENDER,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.CONNEXIONS)
	private addConnexionsItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.CONNEXIONS,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.LENDING_QB)
	private addLendingQBItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.LENDINGQB,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.LANDGORILLA)
	private addLandGorillaItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.LANDGORILLA,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.FICS)
	private addFicsItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.FICS,
				icon: 'fa-file-text-o'
			}
		);
	}

	@Secure(PERMISSION.LENDINGPAD)
	private addLendingpadItem(): void {
		this.logDropdownItems.push(
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.SEPARATOR
			},
			{
				type: DROPDOWN_BUTTON_ITEM_TYPE.OPTION,
				label: LOGS.LENDINGPAD,
				icon: 'fa-file-text-o'
			}
		);
	}

	/**
	 * Open selected log
	 * @param $event
	 */
	onViewLog($event: { index: number; item: DropdownButtonItem }): void {
		const routeParams = {
			route: '',
			id: null,
			openModal: false
		};

		Object.keys(LOGS).forEach(logKey => {
			if ($event.item.label === LOGS[logKey]) {
				const foundKey = Object.keys(URLS).find(urlKey => {
					const pattern = /[^a-zA-Z0-9]/gm;
					const cleanLogKey = logKey.toLowerCase().replace(pattern, '');
					const cleanUrlKey = urlKey.toLowerCase().replace(pattern, '');

					return cleanLogKey === cleanUrlKey;
				});

				if (foundKey) {
					routeParams.route = URLS[foundKey];
					if (Array.isArray(URLS_FIELDS[foundKey])) {
						routeParams.id = [];
						(URLS_FIELDS[foundKey] || []).forEach(itm => routeParams.id.push(this.cellValue[itm]));
					} else {
						routeParams.id = this.cellValue[URLS_FIELDS[foundKey]];
					}
				}
			}
		});

		if (routeParams.route === '') {
			switch($event.item.label) {
				case LOGS.ORDER:
					this.viewOrderLogs();
					routeParams.openModal = true;
					break;
				case LOGS.ADDENDUM:
					Object.assign(routeParams, this.viewAddedumLogs('addendum'));
					break;
				case LOGS.ASSOCIATED_LOGS:
					Object.assign(routeParams, this.viewAddedumLogs('associated'));
					break;
				case LOGS.ASSOCIATED_ADDENDUM_LOGS:
					Object.assign(routeParams, this.viewAddedumLogs('addenassociatedAdddum'));
					break;
			}
		}

		if (!routeParams.openModal) {
			if (!Array.isArray(routeParams.id)) {
				routeParams.id = [routeParams.id];
			}
			this.viewCallBackLogs(routeParams.route, routeParams.id);
		}
	}

	/**
	 * open nexsys log order
	 */
	viewOrderLogs(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: OrdersLogComponent,
				data: {
					isEditing: true,
					product: this.cellValue.id
				},
				modalConfiguration: {
					title: `Order Id ${this.cellValue.id}`,
					modalSize: ModalSize.XL
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result.success) {
					//
				} else {
					// it failed :(
				}
			});
	}

	/**
	 * Open valutrust log order
	 */
	viewAddedumLogs(type: string): any {
		const routeParams = {
			route: this.isValuTrust ? URLS.VALUTRUST : URLS.NEXSYS,
			id: null
		};
		switch (type) {
			case 'addendum':
				routeParams.id = this.cellValue.addendumInfo;
				break;
			case 'associated':
				routeParams.id = this.cellValue.associatedProviderId;
				break;
			case 'associatedAdd':
				routeParams.id = this.cellValue.associatedAddendumProviderId;
				break;
		}

		return routeParams;
	}

	/**
	 * open the full integration callback log order
	 */
	viewCallBackLogs(route: string, id: any): void {
		// console.log(route, id);
		this.router.navigate([
			`/global-configuration/${route}/order-log/`,
			...id,
		]);
	}

	/**
	 * open nexsys log order
	 * @deprecated
	 */
	viewNexsysLogs(): void {
		this.router.navigate([
			`/global-configuration/nexsys-monitor/order-log/`,
			this.cellValue.providerId
		]);
	}

	/**
	 * open valutrust log order
	 * @deprecated
	 */
	viewValuTrustLogs(): void {
		this.router.navigate([
			`/global-configuration/valutrust-monitor/order-log/`,
			this.cellValue.providerId
		]);
	}

	/**
	 * open xpanse log order
	 * @deprecated
	 */
	viewXpanseLogs(): void {
		this.router.navigate([
			'/global-configuration/xpanse-monitor/order-log/',
			this.cellValue.providerTransId,
			this.cellValue.providerId
		]);
	}

	/**
	 * Open FNC log orders
	 * @deprecated
	 */
	viewFNCLogs(): void {
		this.router.navigate([
			`/global-configuration/fnc-monitor/order-log/`,
			this.cellValue.providerId
		]);
	}

	/**
	 * Open EVO log orders
	 * @deprecated
	 */
	viewEVOLogs(): void {
		this.router.navigate([
			`/global-configuration/evo-monitor/order-log/`,
			this.cellValue.providerId
		]);
	}


	/**
	 * open Order External log order
	 * @deprecated
	 */
	 viewOrdersExternalLogs(): void {
		this.router.navigate([
			'/global-configuration/order-external-monitor/order-log/',
			this.cellValue.id,
		]);
	}

	/**
	 * open Valuelink log order
	 * @deprecated
	 */
	viewOrdersValuelinkLogs(): void {
		this.router.navigate([
			`/global-configuration/valuelink-monitor/order-log/`,
			this.cellValue.providerId,
			this.cellValue.providerClientId
		]);
	}

	/**
	 * open Connexions log order
	 * @deprecated
	 */
	viewOrdersConnexionsLogs(): void {
		this.router.navigate([
			`/global-configuration/connexions-monitor/order-log/`,
			this.cellValue.id,
		]);
	}

	/**
	 * open lendingQB log order
	 * @deprecated
	 */
	viewOrdersLendingQBLogs(): void {
		this.router.navigate([
			`/global-configuration/lending-qb-monitor/order-log/`,
			this.cellValue.id,
		]);
	}

	/**
	 * open landGorilla log order
	 * @deprecated
	 */
	viewOrdersLandGorillaLogs(): void {
		this.router.navigate([
			`/global-configuration/landgorilla-monitor/order-log/`,
			this.cellValue.id,
		]);
	}

	/**
	 * Put back the value
	 * @param params
	 */
	refresh(params: ICellRendererParams): boolean {
		this.cellValue = params;
		return true;
	}

	/**
	 * On component destroy
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
